import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Users, Phone, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function EventRsvpDrawer({ event, open, onOpenChange }) {
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !event?.id) return;
    setLoading(true);
    adminApi
      .get(`/admin/events/${event.id}/rsvps`)
      .then((r) => setRsvps(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, event?.id]);

  const active = rsvps.filter((r) => r.status !== "cancelled");
  const totalGuests = active.reduce((acc, r) => acc + (r.party_size || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="event-rsvp-drawer"
        className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
            RSVP Roster
          </div>
          <DialogTitle className="font-display uppercase text-2xl tracking-tight">
            {event?.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--trio-muted)]">
            {event?.event_date}
            {event?.event_time ? ` · ${event.event_time}` : ""} · {active.length}{" "}
            RSVP{active.length === 1 ? "" : "s"} · {totalGuests} guests
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-[var(--trio-primary)]" />
          </div>
        ) : rsvps.length === 0 ? (
          <div className="text-sm text-[var(--trio-muted)] py-8 text-center bg-[var(--trio-surface)] border border-[var(--trio-border)]">
            No RSVPs yet for this event.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--trio-border)]">
            {rsvps.map((r) => (
              <li
                key={r.id}
                data-testid={`drawer-rsvp-${r.id}`}
                className="py-3 grid grid-cols-12 gap-3 items-center"
              >
                <div className="col-span-6">
                  <div className="font-display uppercase text-base tracking-tight">
                    {r.name}
                  </div>
                  <div className="text-xs text-[var(--trio-muted)] flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    <a
                      href={`mailto:${r.email}`}
                      className="inline-flex items-center gap-1 hover:text-[var(--trio-primary)]"
                    >
                      <Mail className="w-3 h-3" /> {r.email}
                    </a>
                    <a
                      href={`tel:${r.phone}`}
                      className="inline-flex items-center gap-1 hover:text-[var(--trio-primary)]"
                    >
                      <Phone className="w-3 h-3" /> {r.phone}
                    </a>
                  </div>
                </div>
                <div className="col-span-3 text-sm">
                  <span className="inline-flex items-center gap-1 text-[var(--trio-text)]">
                    <Users className="w-3.5 h-3.5 text-[var(--trio-primary)]" />
                    {r.party_size}
                  </span>
                </div>
                <div className="col-span-3 text-right">
                  <span
                    className={`text-[10px] tracking-[0.25em] uppercase font-bold border px-2 py-1 ${
                      r.status === "confirmed"
                        ? "bg-emerald-100 text-emerald-900 border-emerald-300"
                        : r.status === "cancelled"
                        ? "bg-red-100 text-red-900 border-red-300"
                        : "bg-amber-100 text-amber-900 border-amber-300"
                    }`}
                  >
                    {r.status}
                  </span>
                </div>
                {r.notes && (
                  <div className="col-span-12 text-xs text-[var(--trio-muted)] italic pl-1">
                    “{r.notes}”
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
